import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { Box, Button, Theme, Tooltip } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import RoundsDescriptionContainer from 'components/user/RoundsDescriptionContainer'
import { ChevronLeftIcon } from 'assets/icons/chevron-left'
import { dataGridStyles } from 'theme/dataGridTheme'
import { rem } from 'theme/materialUITheme'
import { localizedDate, localizedDateTime } from 'utils/formatDate'
import { ChallengeGameFormat, getChallengeTypeName } from 'utils/challenges'
import useParticipantRounds from 'hooks/useParticipantRounds'
import { useSelector } from 'react-redux'
import { RootState, useAppDispatch } from 'store'
import { Link } from 'react-router-dom'
import { ClientRoute } from 'routes/client'
import { SimulatorIcon } from 'assets/icons/round-tags/simulator'
import { TOURNAMENT_TYPE } from 'config'
import { CancelIcon } from 'assets/icons/Cancel'
import { AudienceControl, ChallengeTypeConfig, OverrideRoundEligibilityPayload, UserChallenge } from 'api/types'
import { getChallenge, overrideRoundEligibility } from 'store/challenge/thunks'
import UserChallengeRoundDialog from './UserChallengeRoundDialog'
import { AddIcon } from 'assets/icons/addIcon'
import { PrivateGameIcon } from 'assets/icons/private-game'
import { convertHcpToString } from 'utils/convertHcpToString'

interface Props {
  currentChallenge: UserChallenge
  participantId: string
  userId: number
  participantName?: string
  backLabel?: string
  close: () => void
  refreshChallenges?: () => void
}

const useStyles = makeStyles((theme: Theme) => ({
  evenRow: {
    backgroundColor: '#F5F5F9',
  },
  link: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
}))

const NO_VALUE = '-'

type EligibilityReasonRow = {
  reason?: string
  rule?: string
  eligible?: boolean
}

type UserChallengeRoundRow = {
  id: string
  date: string
  creatorId?: number
  gameName: string
  gameBadges: JSX.Element
  course: string
  roundHcp: string
  gameCreator: string
  gameFormat: string
  score: string | number
  calculatedTime: string
  isQualified: boolean
  override: string
  reasons: EligibilityReasonRow[]
  roundId: string
  gameId?: string
  courseId?: number
  clubId?: number
  clubName: string
}

export default function UserChallengeRounds({
  currentChallenge,
  userId,
  participantId,
  participantName,
  backLabel,
  close,
  refreshChallenges,
}: Props): JSX.Element {
  const intl = useIntl()
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const { userRounds, loading } = useParticipantRounds(currentChallenge.challengeId ?? '-1', participantId, userId)
  const user = useSelector((state: RootState) => state.user.user)
  const { challengeId } = currentChallenge
  const getChallengeDateRange = () => {
    const start = localizedDate(new Date(currentChallenge.schedule?.startAt ?? ''), intl.locale, true)
    const end = localizedDate(new Date(currentChallenge.schedule?.endAt ?? ''), intl.locale, true)
    return `${start} - ${end}`
  }

  const [audienceControl, setAudienceControl] = useState<AudienceControl | undefined>()

  useEffect(() => {
    if (!challengeId) {
      return
    }
    const getChallengeId = async () => {
      const res = await dispatch(getChallenge(challengeId)).unwrap()
      if (!res.data?.audienceControl) {
        return setAudienceControl(undefined)
      }
      setAudienceControl(res.data.audienceControl)
    }

    getChallengeId()
  }, [challengeId])

  const getChallengeVisibility = () => {
    if (!audienceControl) {
      return NO_VALUE
    }
    const { isDiscoverable, joinAudience } = audienceControl
    const isRestricted = !isDiscoverable || !!joinAudience?.countries?.length || !!joinAudience?.clubIds?.length

    return isRestricted ? intl.formatMessage({ id: 'strings.custom' }) : intl.formatMessage({ id: 'strings.global' })
  }

  const getLocationEligibility = () => {
    const { countries, clubIds, courseIds } = currentChallenge.roundEligibility ?? {}
    const isRestricted = !!countries?.length || !!clubIds?.length || !!courseIds?.length

    return isRestricted ? intl.formatMessage({ id: 'strings.custom' }) : intl.formatMessage({ id: 'strings.global' })
  }

  const challengeDescription = {
    challengeId: challengeId ?? '-1',
    challengeName: currentChallenge.info?.name ?? '',
    challengeType: getChallengeTypeName(currentChallenge.typeConfig?.type ?? ChallengeTypeConfig.UNSPECIFIED, intl),
    targetScore: currentChallenge.typeConfig?.target ?? 0,
    challengeLocationEligibility: getLocationEligibility(),
    challengeVisibility: getChallengeVisibility(),
    challengeDuration: getChallengeDateRange(),
    userCurrentScore: currentChallenge.participant?.score ?? 0,
  }

  const [dialogRoundId, setDialogRoundId] = useState<string | null>(null)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 })

  const handleToggleInclusion = async (roundId: string, overrideStatus?: OverrideRoundEligibilityPayload) => {
    if (!currentChallenge.challengeId) {
      return
    }

    const payload = {
      challengeId: currentChallenge.challengeId,
      participantId,
      roundId,
      body: overrideStatus ?? undefined,
    }

    await dispatch(overrideRoundEligibility(payload)).finally(refreshChallenges)
  }

  const renderQualified = (row: UserChallengeRoundRow) => {
    const isDialogOpen = dialogRoundId === row.roundId

    const handleOpenDialog = () => setDialogRoundId(row.roundId)
    const handleCloseDialog = () => setDialogRoundId(null)

    const isExcluded = row.override === 'ROUND_ELIGIBILITY_OVERRIDE_EXCLUDE'

    const openDialogOverride: OverrideRoundEligibilityPayload = {
      override: row.isQualified ? 'ROUND_ELIGIBILITY_OVERRIDE_EXCLUDE' : 'ROUND_ELIGIBILITY_OVERRIDE_UNSPECIFIED',
    }

    return (
      <>
        {isExcluded ? (
          <Button onClick={handleOpenDialog} sx={{ minWidth: 0, padding: 0 }}>
            {!row.isQualified ? (
              <AddIcon />
            ) : (
              <CancelIcon
                style={{
                  color: '#E63946',
                  fontSize: rem(24),
                  cursor: 'pointer',
                }}
              />
            )}
          </Button>
        ) : (
          <Button onClick={handleOpenDialog} sx={{ minWidth: 0, padding: 0 }}>
            <CancelIcon
              style={{
                color: row.isQualified ? '#E63946' : '#A1A1AA',
                fontSize: rem(24),
                cursor: 'pointer',
              }}
            />
          </Button>
        )}

        <UserChallengeRoundDialog
          open={isDialogOpen}
          isExcluded={isExcluded}
          row={row}
          roundId={row.roundId}
          overrideStatus={openDialogOverride.override}
          onClose={handleCloseDialog}
          onToggle={(nextRoundId, overrideStatus) => {
            handleCloseDialog()
            handleToggleInclusion(nextRoundId, overrideStatus)
          }}
        />
      </>
    )
  }

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: intl.formatMessage({ id: 'strings.date' }),
      flex: 1,
      sortable: false,
    },
    {
      field: 'gameName',
      headerName: intl.formatMessage({ id: 'strings.gameName' }),
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'center',
              alignContent: 'center',
              columnGap: 0.5,
              rowGap: 0.5,
              width: '100%',
              minHeight: '100%',
              py: 0.5,
              lineHeight: 1.2,
            }}
          >
            <Link
              className={classes.link}
              to={ClientRoute.ADMIN_GAME_LEADERBOARD(params.row.gameId)}
              style={{
                minWidth: 0,
                maxWidth: '100%',
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                textAlign: 'left',
              }}
            >
              {params.row.gameName}
            </Link>
            {params.row.gameBadges}
          </Box>
        )
      },
    },
    {
      field: 'course',
      headerName: intl.formatMessage({ id: 'strings.course' }),
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Tooltip
          title={`${params.row.clubName}`}
          arrow
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#222A35',
                color: '#FFF',
                fontSize: '16px',
                padding: '20px',
                fontWeight: '400',
              },
            },
            arrow: {
              sx: {
                color: '#222A35',
              },
            },
          }}
        >
          <Link to={ClientRoute.ADMIN_CLUB_COURSE(params.row.clubId, params.row.courseId)}>{params.row.course}</Link>
        </Tooltip>
      ),
    },
    {
      field: 'roundHcp',
      headerName: intl.formatMessage({ id: 'strings.roundHcp' }),
      flex: 1,
      sortable: false,
    },
    {
      field: 'gameCreator',
      headerName: intl.formatMessage({ id: 'strings.gameCreator' }),
      flex: 1,
      sortable: false,
      renderCell: (params: any) => (
        <Link to={ClientRoute.ADMIN_USER(params.row.creatorId)} target="blank">
          {params.row.gameCreator}
        </Link>
      ),
    },
    {
      field: 'gameFormat',
      headerName: intl.formatMessage({ id: 'strings.gameFormat' }),
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
            minHeight: '100%',
            py: 0.5,
            lineHeight: 1.2,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            textAlign: 'left',
          }}
        >
          {ChallengeGameFormat[params.row.gameFormat]}
        </Box>
      ),
    },
    {
      field: 'score',
      headerName: intl.formatMessage({ id: 'strings.score' }),
      flex: 1,
    },
    {
      field: 'calculatedTime',
      headerName: intl.formatMessage({ id: 'strings.calculatedTime' }),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <>
          {params.row.override !== 'ROUND_ELIGIBILITY_OVERRIDE_EXCLUDE' ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                minWidth: 0,
                minHeight: '100%',
                py: 0.5,
                lineHeight: 1.2,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                textAlign: 'left',
              }}
            >
              <Tooltip title="(+00:00 UTC)">{params.row.calculatedTime}</Tooltip>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                minWidth: 0,
                minHeight: '100%',
                py: 0.5,
                lineHeight: 1.2,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                textAlign: 'left',
              }}
            >
              Removed by admin
            </Box>
          )}
        </>
      ),
    },
    {
      field: 'qualified',
      headerName: '',
      flex: 1,
      sortable: false,
      renderCell: (params) => renderQualified(params.row as UserChallengeRoundRow),
      cellClassName: 'qualified-cell',
    },
  ]

  const rows = useMemo(() => {
    const roundIsTmGame = (game: Game): boolean => {
      return Boolean(game.tournamentId)
    }

    const roundIsPrivateGame = (game: Game): boolean => {
      return game.isPrivate === true
    }

    const roundIsHcpRound = (round: UserRound & { gameCreatorName?: string }): boolean => {
      return round.hcpRound === true
    }

    const roundIsRvbRound = (round: UserRound & { gameCreatorName?: string }): boolean => {
      return round.parent?.tournamentType === TOURNAMENT_TYPE.RVB
    }

    const roundIsSimulatorRound = (round: UserRound & { gameCreatorName?: string }): boolean => {
      return round.isSimulator === true
    }

    const roundIsMultiRound = (round: UserRound & { gameCreatorName?: string }): boolean => {
      return round.isMultiround === true
    }

    const getRoundBadges = ({ game, round }: { game: Game; round: UserRound & { gameCreatorName?: string } }) => {
      const isTmGame = roundIsTmGame(game)
      const isPrivateGame = roundIsPrivateGame(game)
      const isHcpRound = roundIsHcpRound(round)
      const isRvbRound = roundIsRvbRound(round)
      const isSimulatorRound = roundIsSimulatorRound(round)

      const isMultiround = roundIsMultiRound(round)

      const badges: RoundBadge[] = [
        { visible: isTmGame, element: <TmBadge key={'tm'}>TM</TmBadge> },
        {
          visible: isPrivateGame,
          element: (
            <PrivateBadge key={'private'}>
              <PrivateGameIcon />
            </PrivateBadge>
          ),
        },
        { visible: isHcpRound, element: <HcpBadge key={'hcp'}>HCP</HcpBadge> },
        { visible: isRvbRound, element: <RvbBadge key={'rvsb'}>RvsB</RvbBadge> },
        { visible: isSimulatorRound, element: <SimulatorBadge key={'simulator'} /> },
        { visible: isMultiround && !isRvbRound, element: <MultiroundBadge key={'multi'}>MR</MultiroundBadge> },
      ]

      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: rem(2) }}>
          {badges.filter((be) => be.visible).map((be) => be.element)}
        </div>
      )
    }
    const getCalculatedTimestamp = (round: (typeof userRounds)[number]) => {
      const time = new Date(String(round.participantRound.lastModified)).getTime()
      return Number.isNaN(time) ? 0 : time
    }

    const sortedRounds = [...userRounds].sort((a, b) => getCalculatedTimestamp(b) - getCalculatedTimestamp(a))

    return sortedRounds.map((round) => {
      const gameRound = round.game?.rounds.find((gameRound) => gameRound.id === String(round.participantRound.roundId))
      const roundCourse = round.game?.courses.find((course) => course.id === gameRound?.courseId)
      const isOwnUserRound = String(round.userRound.roundId) === String(round.participantRound.roundId)
      const roundName = isOwnUserRound ? round.userRound.name : undefined
      const roundGroups = round.game?.groups ?? []
      const roundHcp =
        roundGroups.length > 0
          ? roundGroups
              .find((group) => group.roundId === String(round.participantRound.roundId))
              ?.players?.find((player) => player.userId === userId)?.hcp
          : user.handicap

      return {
        id: round.participantRound.roundId,
        date: round.userRound.startTime
          ? localizedDate(new Date(round.userRound.startTime), intl.locale, true)
          : NO_VALUE,
        gameName: roundName || round.game?.name || NO_VALUE,
        gameBadges: getRoundBadges({ game: round.game, round: round.userRound }),
        course: roundCourse?.name ?? round.game?.courses[0]?.name ?? NO_VALUE,
        roundHcp: roundHcp ? convertHcpToString(roundHcp) : user.handicap,
        gameCreator: round.userRound.gameCreatorName ?? NO_VALUE,
        gameFormat: round.participantRound.primaryGameFormat ?? NO_VALUE,
        score: round.participantRound.score ?? '',
        calculatedTime:
          localizedDateTime(new Date(String(round.participantRound.lastModified)), intl.locale, true) ?? '',
        isQualified: Boolean(round.participantRound?.isQualified),
        override: round.participantRound.override ?? 'ROUND_ELIGIBILITY_OVERRIDE_UNSPECIFIED',
        reasons: (round.participantRound.reasons ?? []).map((reason) => ({
          reason: reason.reason,
          rule: reason.rule,
          eligible: reason.eligible,
        })),
        roundId: round.participantRound.roundId,
        gameId: round.game?.id,
        creatorId: round.userRound.creatorId ?? round.game?.creatorId ?? undefined,
        courseId: gameRound?.courseId ?? round.userRound.courseId ?? undefined,
        clubId: round.userRound.clubId ?? undefined,
        clubName: roundCourse?.clubName ?? round.userRound.clubName ?? undefined,
      }
    })
  }, [userRounds])

  return (
    <PageContainer>
      <GoBack onClick={close}>
        <ChevronLeftIcon width="20px" height="20px" circleFill="#0BB163" pathStroke="#EDEFF1" />
        {backLabel ?? intl.formatMessage({ id: 'strings.backToLeaderboard' })}
      </GoBack>

      {participantName && <ParticipantName>{participantName}</ParticipantName>}

      <RoundsDescriptionContainer challengeDescription={challengeDescription} />

      <GridContainer>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          rowHeight={60}
          loading={loading}
          sx={{
            '.MuiDataGrid-columnHeaderTitleContainer': { fontSize: rem(13), textTransform: 'uppercase' },
            '.qualified-cell': {
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
            },
            ...dataGridStyles,
          }}
          rowSelection={false}
          disableColumnSorting
          disableColumnFilter
          disableColumnMenu
          getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? classes.evenRow : '')}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
        />
      </GridContainer>

      <TimeZoneNote>{intl.formatMessage({ id: 'strings.allTimesShownInUTC' })}</TimeZoneNote>
    </PageContainer>
  )
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  background-color: #fff;
`

const GridContainer = styled.div`
  align-self: stretch;
`

const ParticipantName = styled.h2`
  margin: 0;
  color: #373d40;
  font-size: 18px;
  font-weight: 600;
`

const GoBack = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #098f50;
  font-size: 16px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
`

const TimeZoneNote = styled.span`
  color: #8a8a8e;
  font-size: 15px;
  font-weight: 400;
`

const SimulatorBadge = styled(SimulatorIcon)`
  height: 32px;
  width: 32px;
  margin-left: 10px;
`

const Badge = styled.span`
  color: #fff;
  border-radius: 2px;
  font-weight: bold;
  font-size: 10px;
  line-height: 14px;
  text-align: center;
  padding: 2px 5px;
  margin-left: 5px;
`

const TmBadge = styled(Badge)`
  background-color: ${(props) => props.theme.palette.primary.main};
`

const HcpBadge = styled(Badge)`
  background-color: #0863bc;
`

const RvbBadge = styled(Badge)`
  background-color: #0d2044;
`

const PrivateBadge = styled(Badge)`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background-color: #fcc224;
  border-radius: 4px;

  svg {
    display: block;
    width: 16px;
    height: 17px;
  }
`

const MultiroundBadge = styled(Badge)`
  background-color: #75008f;
`
